import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CInput, SEO } from '../../components/common';
import {
  FiSearch,
  FiHelpCircle,
  FiCreditCard,
  FiShield,
  FiUser,
  FiDollarSign,
  FiChevronRight,
  FiHeadphones,
} from 'react-icons/fi';
import routes from '../../utils/routes';

interface HelpItem {
  id: string;
  icon: any;
  title: string;
  description: string;
}

const HelpCenter: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const [searchText, setSearchText] = useState('');

  const helpItems: HelpItem[] = [
    { id: 'account', icon: FiUser, title: 'Account', description: 'Account settings and profile' },
    { id: 'payments', icon: FiCreditCard, title: 'Payments', description: 'Payment methods and billing' },
    { id: 'security', icon: FiShield, title: 'Security', description: 'Security and privacy settings' },
    { id: 'investments', icon: FiDollarSign, title: 'Investments', description: 'Investment help and guides' },
    { id: 'general', icon: FiHelpCircle, title: 'General', description: 'General questions and help' },
  ];

  const filteredItems = helpItems.filter(
    (item) =>
      item.title.toLowerCase().includes(searchText.toLowerCase()) ||
      item.description.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div
      className="min-h-screen pb-4"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <SEO
        title="Help Center - Yantrai Support"
        description="Get help with your Yantrai account, payments, investments, and security. Browse help categories or search for answers to your questions."
        canonical="/profile/help"
      />
      {/* Gradient Header */}
      <div
        className="p-6 lg:p-8 pb-8"
        style={{
          background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 100%)`,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="mb-4 text-white opacity-80 hover:opacity-100"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-bold m-0 mb-2" style={{ color: colors.white }}>
          Help Center
        </h1>
        <p className="text-sm m-0" style={{ color: colors.white, opacity: 0.8, marginBottom: 16 }}>
          How can we help you today?
        </p>

        <CInput
          value={searchText}
          onChange={setSearchText}
          placeholder="Search for help..."
          insideLeftIcon={() =>
            React.createElement(FiSearch as any, { size: 18, color: colors.grayScale500 })
          }
          bgColor={colors.white}
          textColor={colors.textColor}
        />
      </div>

      <div className="p-4 lg:p-8 -mt-4">
        <div className="lg:max-w-3xl lg:mx-auto">
          {/* FAQ Card */}
          <button
            onClick={() => navigate(routes.faqScreen)}
            className="w-full p-4 rounded-xl mb-6 flex items-center justify-between"
            style={{ backgroundColor: colors.inputBackground }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: colors.primary + '20' }}
              >
                {React.createElement(FiHelpCircle as any, { size: 24, color: colors.primary })}
              </div>
              <div className="text-left">
                <CText type="B16" color={colors.textColor}>
                  Frequently Asked Questions
                </CText>
                <CText type="r12" color={colors.grayScale500}>
                  Find quick answers
                </CText>
              </div>
            </div>
            {React.createElement(FiChevronRight as any, { size: 20, color: colors.grayScale500 })}
          </button>

          {/* Community Section */}
          <CText type="B16" color={colors.textColor} style={{ marginBottom: 8 }}>
            Browse by Category
          </CText>
          <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 16 }}>
            Select a topic to find the help you need
          </CText>

          {/* Help Items */}
          <div className="space-y-3">
            {filteredItems.map((item) => {
              const Icon = item.icon;

              return (
                <button
                  key={item.id}
                  className="w-full flex items-center justify-between p-4 rounded-xl border"
                  style={{
                    backgroundColor: colors.inputBackground,
                    borderColor: colors.borderColor,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: colors.primary + '20' }}
                    >
                      {React.createElement(Icon as any, { size: 20, color: colors.primary })}
                    </div>
                    <div className="text-left">
                      <CText type="M14" color={colors.textColor}>
                        {item.title}
                      </CText>
                      <CText type="r12" color={colors.grayScale500}>
                        {item.description}
                      </CText>
                    </div>
                  </div>
                  {React.createElement(FiChevronRight as any, { size: 20, color: colors.grayScale500 })}
                </button>
              );
            })}
          </div>

          {/* Contact Support FAB */}
          <button
            className="fixed bottom-8 right-4 lg:right-8 w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
            style={{ backgroundColor: colors.primary }}
          >
            {React.createElement(FiHeadphones as any, { size: 24, color: colors.white })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
